import React, {useMemo} from 'react';
import {Partner} from '@/Pages/Partners/store/types';
import ErrorSection from '../ErrorSection/ErrorSection';
import {ErrorItem} from '../ErrorSection/ErrorSection.types';
import {useInvalidCostFactorHelpers} from '../useInvalidCostFactorHelpers';
import {
  isRegionalOrBusPartnerErrorCode,
  getRegionalOrBusPartnerLevelError,
  RegionalOrBusPartnerErrorCode
} from '../InvalidCostFactorsPopup.utils';
import {BusPartnerErrorsSectionProps} from './BusPartnerErrorsSection.types';

const BusPartnerErrorsSection: React.FC<BusPartnerErrorsSectionProps> = ({
  invalidCostFactors,
  busValues,
  busPartners,
  countries,
  costYear
}) => {
  const {getVehicleContext, countriesMap, logMissing} =
    useInvalidCostFactorHelpers(busValues, busPartners, countries);

  const busPartnerErrors = useMemo(() => {
    if (!costYear) return [];

    const errors = (invalidCostFactors ?? []).filter(invalidCostFactor =>
      isRegionalOrBusPartnerErrorCode(invalidCostFactor.errorCode)
    );
    const bpIdToErrorCodes = new Map<string, RegionalOrBusPartnerErrorCode[]>();

    const groupedBusPartnerErrors: Map<
      string,
      {busPartner: Partner; errors: ErrorItem[]}
    > = new Map();

    for (const error of errors) {
      const context = getVehicleContext(error);
      if (!context) continue;

      const {busPartner} = context;

      // Avoid duplicates
      const prevErrorCodes = bpIdToErrorCodes.get(busPartner.id) ?? [];
      const currErrorCode = error.errorCode as RegionalOrBusPartnerErrorCode;
      if (prevErrorCodes.includes(currErrorCode)) continue;
      bpIdToErrorCodes.set(busPartner.id, [...prevErrorCodes, currErrorCode]);

      // Find bus partner country
      const busPartnerCountry = countriesMap.get(busPartner.countryId);
      if (!busPartnerCountry) {
        logMissing('bus partner country', error);
        continue;
      }

      const errorMessage = getRegionalOrBusPartnerLevelError(
        currErrorCode,
        busPartner,
        busPartnerCountry,
        costYear
      );

      const group = groupedBusPartnerErrors.get(busPartner.id) ?? {
        busPartner,
        errors: [] as ErrorItem[]
      };
      group.errors.push({code: currErrorCode, message: errorMessage});
      groupedBusPartnerErrors.set(busPartner.id, group);
    }
    return Array.from(groupedBusPartnerErrors.values());
  }, [
    costYear,
    invalidCostFactors,
    getVehicleContext,
    countriesMap,
    logMissing
  ]);

  if (busPartnerErrors.length === 0) return null;

  return (
    <>
      {busPartnerErrors.map(({busPartner, errors: errorList}) => (
        <ErrorSection
          key={`bus-partner-${busPartner.id}`}
          id={`bus-partner-${busPartner.id}`}
          title={
            <>
              The following costs for {busPartner.name} are missing. You can
              either update this on bus partner or regional level.
            </>
          }
          errors={errorList}
        />
      ))}
    </>
  );
};

export default React.memo(BusPartnerErrorsSection);
